import type { LagSimulationConfig } from '@/app/sdk';

import { sdk } from '@/app/sdk';

/**
 * Manages the lifecycle of a multiplayer server process via the Electron SDK
 */
class MultiplayerServerManager {
  /**
   * Start the multiplayer server process
   * @param projectFolder - The absolute path to the project folder
   * @param projectName - The name of the project
   * @param scenePath - Optional scene path to load (if not provided, uses default server scene)
   * @returns Promise that resolves when the server is ready
   */
  async startServer(projectFolder: string, projectName: string, scenePath?: string | null, lagSimulationConfig?: LagSimulationConfig): Promise<void> {
    if (!sdk) {
      throw new Error('SDK not available - cannot start multiplayer server');
    }

    console.log('[MultiplayerServerManager] Starting server for project:', projectName);
    console.log('[MultiplayerServerManager] Project folder:', projectFolder);
    console.log('[MultiplayerServerManager] Scene path:', scenePath ?? '(using default)');
    console.log('[MultiplayerServerManager] Lag simulation:', lagSimulationConfig ?? '(disabled)');

    const error = await sdk.multiplayerServer.start(projectFolder, projectName, scenePath ?? undefined, lagSimulationConfig);
    if (error) {
      console.error('[MultiplayerServerManager] Failed to start server:', error);
      throw error;
    }

    console.log('[MultiplayerServerManager] ✓ Server started successfully');
  }

  /**
   * Stop the multiplayer server process
   */
  async stopServer(): Promise<void> {
    if (!sdk) {
      console.warn('[MultiplayerServerManager] SDK not available - cannot stop server');
      return;
    }

    console.log('[MultiplayerServerManager] Stopping server...');

    const error = await sdk.multiplayerServer.stop();
    if (error) {
      console.error('[MultiplayerServerManager] Error stopping server:', error);
      throw error;
    }

    console.log('[MultiplayerServerManager] ✓ Server stopped successfully');
  }

  /**
   * Check if the server is currently running
   */
  async isRunning(): Promise<boolean> {
    if (!sdk) {
      return false;
    }

    const status = await sdk.multiplayerServer.status();
    return status.isRunning;
  }
}

// Export a singleton instance
export const multiplayerServerManager = new MultiplayerServerManager();

